import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, ArrowRight, Building2 } from "lucide-react";

const KEY = "tr_toothmatrix_banner_v1";

/**
 * Dismissible top banner promoting Toothmatrix to public visitors.
 *
 * Behaviour:
 *   - hides itself on /toothmatrix (would be redundant), /login, /portal/*, /admin/*
 *     and any case-detail / private route.
 *   - hidden once dismissed; the dismissal state is stored in localStorage
 *     so it persists across sessions.
 *   - fades+slides in on first mount; uses `prefers-reduced-motion` to
 *     skip the animation for accessibility.
 */
export default function ToothmatrixBanner() {
    const { pathname } = useLocation();
    const [dismissed, setDismissed] = useState(true);

    useEffect(() => {
        try {
            setDismissed(localStorage.getItem(KEY) === "1");
        } catch (err) {
            // localStorage can throw in private mode / sandbox iframes — assume not-dismissed.
            console.warn("[banner] read failed:", err?.message);
            setDismissed(false);
        }
    }, []);

    // Routes where the banner would be noisy / off-brand.
    const HIDE_PREFIXES = ["/toothmatrix", "/login", "/portal", "/admin", "/cases", "/dental-automaton", "/solutions", "/practice"];
    const shouldHide = HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
    if (shouldHide || dismissed) return null;

    const dismiss = () => {
        setDismissed(true);
        try { localStorage.setItem(KEY, "1"); }
        catch (err) { console.warn("[banner] save failed:", err?.message); }
    };

    return (
        <div
            data-testid="toothmatrix-banner"
            className="relative bg-gradient-to-r from-[#0B1120] via-[#0E2E3A] to-[#0B1120] text-white border-b border-cyan-500/15 motion-safe:animate-[tr-banner-in_300ms_ease-out]"
        >
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-center gap-3 text-center text-[12.5px] sm:text-[13px] leading-tight">
                <span className="hidden sm:inline-flex items-center gap-1.5 font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-300/90">
                    <Sparkles size={11} /> New
                </span>
                <span className="text-white/90">
                    <Building2 size={12} className="inline-block mb-0.5 mr-1 text-cyan-300/90" />
                    Run your lab on <strong className="text-white">Toothmatrix</strong>.
                </span>
                <Link
                    to="/toothmatrix"
                    data-testid="banner-cta"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-400 text-[#0B1120] font-medium hover:bg-cyan-300 transition text-[12px]"
                >
                    Request a demo <ArrowRight size={11} />
                </Link>
            </div>
            <button
                onClick={dismiss}
                data-testid="banner-dismiss"
                aria-label="Dismiss announcement"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition"
            >
                <X size={13} />
            </button>
            <style>{`@keyframes tr-banner-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
        </div>
    );
}
